import React from 'react';

//Hooks
import {useEffect} from 'react';

//Components
import {ChevronLeft, ChevronRight} from "react-feather";

//Styles
import Styles from './Calendar.module.scss';

//Utils
import moment from 'moment';
import PropTypes from 'prop-types';

const Calendar = React.forwardRef(({month, year, showPastDates = true, selectedDate, onSelectDate, onChangeTimespan, disabledDates = [], disabledDays = [], maxDate = null}, ref) => {
    const monthDate = moment().utc().month(month).year(year);
    const weekdays = moment.weekdaysMin();
    weekdays.push(weekdays.shift());

    const dates = [];
    const date = monthDate.clone().startOf('month').clone().startOf('isoWeek');
    const end = monthDate.clone().endOf('month').clone().endOf('isoWeek');
    end.hour(0);
    end.minute(0);
    end.second(0);
    end.millisecond(0);
    const today = moment();
    today.hour(0);
    today.minute(0);
    today.second(0);
    today.millisecond(0);

    const to = !!maxDate ? moment(maxDate) : null;

    useEffect(() => {
        if(!onChangeTimespan || !maxDate || month < moment(maxDate).month() || year < moment(maxDate).year()) {
            return;
        }

        onChangeTimespan(moment(maxDate).month(), moment(maxDate).year());
    }, [maxDate, month, year, onChangeTimespan]);

    while (true) {
        let day = date.clone();
        const disabled = disabledDates.find(date => day.isSame(date)) || disabledDays.indexOf(day.isoWeekday() - 1) !== -1 || day.isAfter(to, 'day');
        dates.push(
            <div key={`day-${day.month()}-${day.date()}`}
                 className={'d-flex justify-content-center align-items-center'}>
                <button
                    data-testid={`calendar-date-button-${day.format('YYYY-MM-DD')}`}
                    className={[
                        Styles.day,
                        day.isBefore(today) || day.month() !== month || disabled ? Styles.disabled : Styles.available,
                        day.isSame(selectedDate) ? Styles.selected : undefined
                    ].join(' ')}
                    onClick={() => {
                        if(day.isBefore(today) || day.month() !== month || disabled) {
                            return;
                        }

                        onSelectDate(day)
                    }}
                >
                    <span>
                        {day.date()}
                    </span>
                </button>
            </div>
        );
        if (day.isSame(end)) {
            break;
        }
        date.add(1, 'day');
    }

    return (
        <div ref={ref} className={Styles.container}>
            <div className={Styles.header}>
                {(showPastDates || year > moment().year() || month > moment().month()) && (
                    <button className={Styles.chevronButton} onClick={() => {
                        onChangeTimespan(month - 1 < 0 ? 11 : month - 1, year - (month - 1 < 0 ? 1 : 0))
                    }}>
                        <ChevronLeft size={28} className={Styles.chevron}/>
                    </button>
                )}

                <span className={Styles.title}>
                    {`${monthDate.startOf('month').date()} - ${monthDate.endOf('month').date()} ${monthDate.format('MMMM')}`}
                </span>

                {(!to || to.year() > year || to.month() > month) && (
                    <button className={Styles.chevronButton} onClick={() => {
                        onChangeTimespan(month + 1 > 11 ? 0 : month + 1, year + (month + 1 > 11 ? 1 : 0))
                    }}>
                        <ChevronRight size={28} className={Styles.chevron}/>
                    </button>
                )}
            </div>

            <div className={Styles.content}>
                {weekdays
                    .map(day => day.substr(0, 1))
                    .map((weekday, i) => <span key={`weekday-${i}`} className={Styles.weekday}>{weekday}</span>)}

                <div className={Styles.line}/>

                {dates}
            </div>
        </div>
    )
});

Calendar.propTypes = {
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    showPastDates: PropTypes.bool,
    selectedDate: PropTypes.any,
    onSelectDate: PropTypes.func.isRequired,
    onChangeTimespan: PropTypes.func.isRequired,
    disabledDates: PropTypes.array,
    disabledDays: PropTypes.arrayOf(PropTypes.number),
    maxDate: PropTypes.any
};

export default Calendar;
